import { GenericSelect } from '@/components/GenericSelect';
import { checkMaterialCreateInput, CreateMaterialInputType } from '@shelflix/schemas/material/create';
import { DisplayDto } from '@shelflix/trpc/routes/material/listDisplays/useCase';
import { Badge } from '@shelflix/ui-web/components/ui/badge';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@shelflix/ui-web/components/ui/form';
import { Input } from '@shelflix/ui-web/components/ui/input';
import { useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';

interface DisplayInfoSectionProps {
  form: UseFormReturn<CreateMaterialInputType>;
  displays: DisplayDto[];
}

export function DisplayInfoSection({ form, displays }: DisplayInfoSectionProps) {
  const code = form.watch('display.code');
  const currentDisplay = displays.find((d) => d.code === code);

  useEffect(() => {
    if (currentDisplay) {
      form.setValue('display.code', currentDisplay.code);
      form.setValue('display.brand', currentDisplay.brand);
      form.setValue('display.model', currentDisplay.deviceModelName);
      form.setValue('display.orientation', currentDisplay.screenOrientation);
      form.setValue('display.type', currentDisplay.deviceType);
      form.setValue('display.size', currentDisplay.screenSize);
      form.setValue('display.externalId', currentDisplay.id);
    } else {
      form.resetField('display.code');
      form.resetField('display.brand');
      form.resetField('display.model');
      form.resetField('display.orientation');
      form.resetField('display.size');
      form.resetField('display.type');
      form.resetField('display.externalId');
    }
  }, [currentDisplay]);

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex w-full flex-col items-start gap-4 sm:flex-row">
        <FormField
          control={form.control}
          name="display.code"
          rules={{ validate: (value) => checkMaterialCreateInput('display.code', value) }}
          render={({ field: _field }) => (
            <FormItem className="flex w-full flex-col gap-[3px]">
              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Identificador da tela</FormLabel>
              <FormControl>
                <GenericSelect
                  listOnEmptySearch
                  dependencies={[displays]}
                  fetchItems={(search) => {
                    const items = displays
                      .map((d) => ({
                        ...d,
                        disabled: !!d.currentMaterialAssociation,
                        externalId: d.id,
                      }))
                      .sort((a, b) => {
                        if (a.currentMaterialAssociation && !b.currentMaterialAssociation) return 1;
                        if (!a.currentMaterialAssociation && b.currentMaterialAssociation) return -1;
                        return 0;
                      });
                    return !search
                      ? Promise.resolve(items)
                      : Promise.resolve(items.filter((d) => d.code.toLowerCase().includes(search.toLowerCase())));
                  }}
                  onSearchChange={() => {}}
                  onSelect={(item: DisplayDto) => {
                    if (item.currentMaterialAssociation) return;
                    form.setValue('display.code', item.code);
                    form.setValue('display.brand', item.brand);
                    form.setValue('display.model', item.deviceModelName);
                    form.setValue('display.orientation', item.screenOrientation);
                    form.setValue('display.type', item.deviceType);
                    form.setValue('display.size', item.screenSize);
                    form.setValue('display.externalId', item.id);
                    form.trigger('display');
                  }}
                  renderItem={(item: DisplayDto) => (
                    <div
                      className={`flex w-full flex-col gap-2 ${item.currentMaterialAssociation ? 'cursor-disabled opacity-40' : ''}`}
                    >
                      <div className="flex w-full items-center">
                        <span>{item.code}</span>
                        <div className="ml-auto flex flex-col">
                          <Badge variant={item.status.toUpperCase() === 'ACTIVE' ? 'default' : 'destructive'}>
                            {item.status.toUpperCase() === 'ACTIVE' ? 'ONLINE' : item.status}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex w-full items-center">
                        {item.currentMaterialAssociation && (
                          <div>
                            <span className="text-[10px]">
                              Dispositivo em uso em {item.currentMaterialAssociation.facilityName}
                              {item.currentMaterialAssociation.exhibitionPoint
                                ? ` (${item.currentMaterialAssociation.exhibitionPoint})`
                                : ''}{' '}
                              - {item.currentMaterialAssociation.code}
                            </span>
                          </div>
                        )}
                        {!item.currentMaterialAssociation && (
                          <div>
                            <span className="text-[13px] text-[var(--active-dark)]">Disponível</span>
                          </div>
                        )}
                        <span className="ml-auto flex">{item.id}</span>
                      </div>
                    </div>
                  )}
                  getItemLabel={(item: DisplayDto) => item.code}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="display.brand"
          rules={{ validate: (value) => checkMaterialCreateInput('display.brand', value) }}
          render={({ field }) => (
            <FormItem className="flex w-full flex-col gap-[3px]">
              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Marca do Monitor</FormLabel>
              <FormControl>
                <Input disabled className="h-10" placeholder="Marca X" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="display.model"
          rules={{ validate: (value) => checkMaterialCreateInput('display.model', value) }}
          render={({ field }) => (
            <FormItem className="flex w-full flex-col gap-[3px]">
              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Modelo</FormLabel>
              <FormControl>
                <Input disabled className="h-10" placeholder="Ex: LG 556UH-F" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="flex w-full flex-col items-start gap-4 sm:flex-row">
        <FormField
          control={form.control}
          name="display.orientation"
          rules={{ validate: (value) => checkMaterialCreateInput('display.orientation', value) }}
          render={({ field }) => (
            <FormItem className="flex w-full flex-col gap-[3px]">
              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Orientação</FormLabel>
              <FormControl>
                <Input disabled className="h-10" placeholder="Selecione uma opção" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="display.externalId"
          rules={{ validate: (value) => checkMaterialCreateInput('display.externalId', value) }}
          render={({ field }) => (
            <FormItem className="flex w-full flex-col gap-[3px]">
              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">MacAddress Dispositivo</FormLabel>
              <FormControl>
                <Input disabled className="h-10" placeholder="Selecione uma opção" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="display.size"
          render={({ field }) => (
            <FormItem className="flex w-full flex-col gap-[3px]">
              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Tamanho de Tela</FormLabel>
              <FormControl>
                <Input disabled className="h-10" placeholder="Ex: 40" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
